import { Attr, BelongsTo, HasMany, Model } from "spraypaint";

import {
  ApplicationRecord,
  objectDirtyChecker,
} from "shared/services/spraypaint";

import type Attachment from "./Attachment";
import type Organisation from "./Organisation";
import type PodcastEpisode from "./PodcastEpisode";
import type ProgramAiring from "./ProgramAiring";
import type SourceRecord from "./SourceRecord";
import type TranscriptRequestSourceFile from "./TranscriptRequestSourceFile";
import type User from "./User";
import type ViewedItem from "./ViewedItem";

@Model()
class TranscriptRequest extends ApplicationRecord {
  static jsonapiType = "transcript_requests";

  @BelongsTo() accountManager: User;

  @Attr() accountManagerId: number;

  @HasMany() attachments: Attachment[];

  @Attr() billable: boolean;

  @Attr() body: string;

  @Attr() completedAt: string;

  @Attr({ persist: false }) createdAt: string;

  @BelongsTo() creator: User;

  @Attr({ persist: false }) docxFileUrl: string;

  @Attr() endTime: number;

  @Attr({ dirtyChecker: objectDirtyChecker }) fileUrl: any;

  @Attr({ dirtyChecker: objectDirtyChecker }) formats: any;

  @Attr() instantDownload: boolean;

  @Attr() lockVersion: number;

  @HasMany() manualAttachments: Attachment[];

  @Attr() manualTranscriptionNotes: string;

  @Attr() manualTranscriptionStatus: string;

  @Attr({ persist: false }) medium: string;

  @Attr() notes: string;

  @BelongsTo() organisation: Organisation;

  @Attr() organisationId: number;

  @Attr() organisationTeamId: number;

  @Attr() originalId: number;

  @Attr() originId: number;

  @Attr({ persist: false }) originLabel: string;

  @Attr() originType: string;

  @Attr({ persist: false }) pdfFileUrl: string;

  @Attr({ persist: false }) playerUrl: string;

  @BelongsTo() podcastEpisode: PodcastEpisode;

  @Attr() podcastEpisodeId: number;

  @BelongsTo() programAiring: ProgramAiring;

  @Attr({ dirtyChecker: objectDirtyChecker }) recipients: any;

  @BelongsTo() secondaryAccountManager: User;

  @Attr() secondaryAccountManagerId: number;

  @BelongsTo() source: SourceRecord;

  @Attr() sourceId: number;

  @Attr() sourceName: string;

  @Attr() sourceType: string;

  @Attr() startTime: number;

  @Attr() status: string;

  @Attr() summary: string;

  @Attr() templateId: number;

  @Attr({ persist: false }) thumbnail: string;

  @Attr() timeZone: string;

  @Attr() title: string;

  @HasMany() transcriptionAttachments: Attachment[];

  @Attr() transcriptionMethod: string;

  @Attr({ persist: false }) transcriptionPlatformUrl: string;

  @HasMany()
  transcriptRequestSourceFiles: TranscriptRequestSourceFile[];

  @Attr() turnaroundMinutes: number;

  @Attr({ persist: false }) updatedAt: string;

  @BelongsTo() user: User;

  @Attr() userId: number;

  @HasMany({ name: "target" }) viewedItems: ViewedItem[];

  handlerName() {
    return this.accountManager?.name;
  }

  inProgress(statusObject: any) {
    const statusSource = statusObject || this;

    return (
      statusSource.status !== "Completed" &&
      ((this.transcriptionMethod === "automated" &&
        statusSource.status !== "Ready") ||
        (this.transcriptionMethod === "professional" &&
          statusSource.manualTranscriptionStatus !== "Delivered"))
    );
  }

  requestedByUser() {
    if (this.creator && this.user) {
      return this.creator.id === this.user.id;
    }

    return false;
  }

  secondaryHandlerName() {
    return this.secondaryAccountManager?.name;
  }
}

export default TranscriptRequest;
